import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { WorkerLayout } from '../../components/layouts/WorkerLayout';
import { MapPin, Clock, Play, Plus, Minus, Check, ArrowLeft, Send, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { PhotoCapture } from '../../components/ui/photo-capture';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const WorkerJob = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [job, setJob] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [materialsUsed, setMaterialsUsed] = useState([]);
  const [observations, setObservations] = useState('');
  const [photos, setPhotos] = useState([]);
  const [reportType, setReportType] = useState('normal');
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [linkedIncidentId, setLinkedIncidentId] = useState('');
  const [incidents, setIncidents] = useState([]);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      const [jobRes, templatesRes, materialsRes, incidentsRes] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get('/report-templates'),
        api.get('/materials'),
        api.get('/work-reports/incidents')
      ]);
      setJob(jobRes.data);
      setMaterials(materialsRes.data);
      setIncidents(incidentsRes.data || []);
      
      const defaultTemplate = templatesRes.data.find(t => t.is_default) || templatesRes.data[0];
      if (defaultTemplate) {
        const checklistItems = (defaultTemplate.checklist_items || []).map((item, idx) => ({
          id: `check-${idx}`,
          label: item,
          checked: false
        }));
        setChecklist(checklistItems);
        
        const paramItems = (defaultTemplate.parameters || []).map((param, idx) => ({
          id: `param-${idx}`,
          name: param.name,
          value: '',
          value_before: '',
          value_after: '',
          unit: param.unit || ''
        }));
        setParameters(paramItems);
      }
    } catch (error) {
      toast.error('Error al cargar el trabajo');
      navigate('/worker');
    } finally {
      setLoading(false);
    }
  };

  const startJob = async () => {
    try {
      await api.patch(`/jobs/${jobId}/status`, null, { params: { status: 'in_progress' } });
      setStarted(true);
      setJob(prev => ({ ...prev, status: 'in_progress' }));
      toast.success('Trabajo iniciado');
    } catch (error) {
      toast.error('Error al iniciar el trabajo');
    }
  };

  const handleChecklistChange = (id, checked) => {
    setChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, checked } : item
    ));
  };

  const handleParameterChange = (id, value) => {
    setParameters(prev => prev.map(param => 
      param.id === id ? { ...param, value } : param
    ));
  };

  const handleAddMaterial = (materialId) => {
    const material = materials.find(m => m.id === materialId);
    if (!material) return;
    
    const existing = materialsUsed.find(m => m.material_id === materialId);
    if (existing) {
      setMaterialsUsed(prev => prev.map(m => 
        m.material_id === materialId ? { ...m, quantity: m.quantity + 1 } : m
      ));
    } else {
      setMaterialsUsed(prev => [...prev, {
        material_id: materialId,
        name: material.name,
        quantity: 1,
        unit: material.unit
      }]);
    }
  };

  const handleMaterialQuantity = (materialId, delta) => {
    setMaterialsUsed(prev => {
      const updated = prev.map(m => {
        if (m.material_id === materialId) {
          const newQty = Math.max(0, m.quantity + delta);
          return { ...m, quantity: newQty };
        }
        return m;
      });
      return updated.filter(m => m.quantity > 0);
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Transform checklist to match backend model (label, checked instead of label, checked which was id based)
      const transformedChecklist = checklist.map(item => ({
        label: item.label,
        checked: item.checked
      }));
      
      // Transform parameters to match backend model
      const transformedParameters = parameters.map(p => ({
        name: p.name,
        value: showBeforeAfter ? '' : p.value,
        value_before: showBeforeAfter ? p.value_before : null,
        value_after: showBeforeAfter ? p.value_after : null,
        unit: p.unit || ''
      }));
      
      const reportData = {
        job_id: jobId,
        checklist: transformedChecklist,
        parameters: transformedParameters,
        materials_used: materialsUsed,
        observations: observations,
        photos: photos,
        started_at: new Date().toISOString(), // Set start time when submitting
        report_type: reportType,
        send_email: sendEmail,
        linked_incident_id: linkedIncidentId || null
      };
      
      await api.post('/work-reports', reportData);
      toast.success(reportType === 'incidencia' ? 'Incidencia registrada' : 'Parte de trabajo enviado correctamente');
      navigate('/worker');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el parte');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <WorkerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </WorkerLayout>
    );
  }

  const isJobInProgress = started || job?.status === 'in_progress';
  const isJobCompleted = job?.status === 'completed';

  return (
    <WorkerLayout>
      <div className="p-4 space-y-4" data-testid="worker-job-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/worker')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-xl font-bold">{job?.title}</h1>
              {job?.is_emergency && <Badge className="bg-red-500 text-white">URGENTE</Badge>}
            </div>
            <p className="text-sm text-slate-500">{job?.client_name}</p>
          </div>
        </div>

        {/* Job Info */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-4 h-4" />
              <span>{job?.client_address}</span>
            </div>
            {job?.scheduled_time && (
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span>{job?.scheduled_time}</span>
              </div>
            )}
            {job?.description && (
              <p className="text-sm text-slate-500 pt-2 border-t">{job?.description}</p>
            )}
          </CardContent>
        </Card>

        {/* Start Button */}
        {!isJobInProgress && !isJobCompleted && (
          <Button className="w-full h-14 text-lg" onClick={startJob} data-testid="start-job-btn">
            <Play className="w-6 h-6 mr-2" />
            Iniciar trabajo
          </Button>
        )}

        {/* Work Report Form */}
        {isJobInProgress && !isJobCompleted && (
          <div className="space-y-4">
            {/* Checklist */}
            {checklist.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Checklist</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {checklist.map(item => (
                    <div key={item.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Checkbox
                        id={item.id}
                        checked={item.checked}
                        onCheckedChange={(checked) => handleChecklistChange(item.id, checked)}
                        className="mt-1"
                      />
                      <label htmlFor={item.id} className="flex-1 cursor-pointer">
                        <span className={item.checked ? 'line-through text-slate-400' : ''}>
                          {item.label}
                        </span>
                      </label>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Report Type Selection */}
            <Card className={reportType === 'incidencia' ? 'border-red-300 bg-red-50' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className={`w-5 h-5 ${reportType === 'incidencia' ? 'text-red-600' : 'text-slate-400'}`} />
                  Tipo de Parte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={reportType === 'normal' ? 'default' : 'outline'}
                    size="sm"
                    className={reportType === 'normal' ? 'bg-sky-600 hover:bg-sky-700' : ''}
                    onClick={() => setReportType('normal')}
                  >
                    Normal
                  </Button>
                  <Button
                    type="button"
                    variant={reportType === 'incidencia' ? 'default' : 'outline'}
                    size="sm"
                    className={reportType === 'incidencia' ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => setReportType('incidencia')}
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Incidencia
                  </Button>
                </div>
                {reportType === 'incidencia' && (
                  <p className="text-sm text-red-700">
                    Usa incidencia para reportar problemas o cosas que están mal.
                  </p>
                )}
                
                {/* Before/After toggle */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div>
                    <Label className="text-sm font-medium">Antes/Después</Label>
                    <p className="text-xs text-slate-500">Valores antes y después del tratamiento</p>
                  </div>
                  <Switch
                    checked={showBeforeAfter}
                    onCheckedChange={setShowBeforeAfter}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Parameters */}
            {parameters.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Parámetros</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {showBeforeAfter ? (
                    // Before/After mode
                    parameters.map(param => (
                      <div key={param.id} className="space-y-2 p-3 bg-slate-50 rounded-lg">
                        <Label className="font-medium">{param.name} {param.unit && `(${param.unit})`}</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Antes</Label>
                            <Input
                              type="text"
                              value={param.value_before || ''}
                              onChange={(e) => {
                                const newParams = parameters.map(p =>
                                  p.id === param.id ? { ...p, value_before: e.target.value } : p
                                );
                                setParameters(newParams);
                              }}
                              placeholder="Valor"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Después</Label>
                            <Input
                              type="text"
                              value={param.value_after || ''}
                              onChange={(e) => {
                                const newParams = parameters.map(p =>
                                  p.id === param.id ? { ...p, value_after: e.target.value } : p
                                );
                                setParameters(newParams);
                              }}
                              placeholder="Valor"
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    // Normal mode
                    parameters.map(param => (
                      <div key={param.id} className="flex items-center gap-3">
                        <Label className="w-32">{param.name}</Label>
                        <Input
                          type="text"
                          value={param.value}
                          onChange={(e) => handleParameterChange(param.id, e.target.value)}
                          placeholder="Valor"
                          className="flex-1"
                          data-testid={`param-${param.name}-input`}
                        />
                        {param.unit && <span className="text-slate-500 w-12">{param.unit}</span>}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            )}

            {/* Materials */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Material usado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {materialsUsed.map(mat => (
                  <div key={mat.material_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span>{mat.name}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleMaterialQuantity(mat.material_id, -1)}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-16 text-center font-medium">
                        {mat.quantity} {mat.unit}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleMaterialQuantity(mat.material_id, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {materials.length > 0 && (
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddMaterial(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-full p-3 border rounded-lg text-slate-500"
                    data-testid="add-material-select"
                  >
                    <option value="">+ Añadir material</option>
                    {materials.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                )}
              </CardContent>
            </Card>

            {/* Observations */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Observaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={4}
                  placeholder="Escribe cualquier observación..."
                  data-testid="observations-input"
                />
              </CardContent>
            </Card>

            {/* Photos */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Fotos ({photos.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <PhotoCapture 
                  photos={photos}
                  onPhotosChange={setPhotos}
                  maxPhotos={10}
                  disabled={submitting}
                />
              </CardContent>
            </Card>

            {/* Link to incident (for normal/repair reports) */}
            {reportType === 'normal' && incidents.length > 0 && (
              <Card>
                <CardContent className="pt-6 space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Vincular con incidencia
                  </Label>
                  <p className="text-xs text-slate-500">Si este parte repara una incidencia previa, selecciónala</p>
                  <Select value={linkedIncidentId || "none"} onValueChange={(v) => setLinkedIncidentId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="select-linked-incident">
                      <SelectValue placeholder="Sin vincular" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin vincular</SelectItem>
                      {incidents.filter(i => !i.linked_repair_id).map(inc => (
                        <SelectItem key={inc.id} value={inc.id}>
                          {inc.client_name} - {inc.completed_at ? new Date(inc.completed_at).toLocaleDateString('es-ES') : ''} - {(inc.observations || '').slice(0, 40)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Send Email Toggle */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Send className="w-4 h-4 text-sky-500" />
                      Enviar al cliente por email
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">Se enviará el PDF del parte al email del cliente</p>
                  </div>
                  <Switch
                    checked={sendEmail}
                    onCheckedChange={setSendEmail}
                    data-testid="send-email-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              className="w-full h-14 text-lg"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-report-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Enviar parte de trabajo
                </>
              )}
            </Button>
          </div>
        )}

        {/* Completed */}
        {isJobCompleted && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <h3 className="font-semibold text-green-800">Trabajo completado</h3>
              <p className="text-sm text-green-600 mt-1">El parte de trabajo ya ha sido enviado</p>
            </CardContent>
          </Card>
        )}
      </div>
    </WorkerLayout>
  );
};

export default WorkerJob;
